import { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import type { ReactNode } from 'react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface StageEvent {
  stage: number
  name: string
  status: 'running' | 'complete' | 'error' | 'skipped'
  label: string
  [key: string]: any
}

interface PipelineState {
  status: 'idle' | 'running' | 'complete' | 'error'
  stages: StageEvent[]
  result: any | null
  error: string | null
  currentStage: number
}

interface BTRAInvestigationPanelProps {
  query: string
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STAGE_DEFS = [
  { stage: 1, name: 'Breach Intelligence Fetch' },
  { stage: 2, name: 'Temporal Attribute Tagging' },
  { stage: 3, name: 'Deconfliction Engine' },
  { stage: 4, name: 'Identity Clustering' },
  { stage: 5, name: 'Pivot Orchestration' },
  { stage: 6, name: 'Attribution Scoring' },
  { stage: 7, name: 'Report Compilation' },
]

const TABS = [
  { id: 'overview',  label: 'OVERVIEW' },
  { id: 'identity',  label: 'IDENTITY & CONTACT' },
  { id: 'digital',   label: 'DIGITAL & CORPORATE' },
  { id: 'threats',   label: 'THREATS & NETWORK' },
  { id: 'evidence',  label: 'EVIDENCE CHAIN' },
]

// ── Utility helpers ───────────────────────────────────────────────────────────

function getToken() { return localStorage.getItem('auth_token') || '' }

function scoreColor(pct: number) {
  return pct >= 75 ? '#22c55e' : pct >= 60 ? '#eab308' : pct >= 45 ? '#f97316' : '#ef4444'
}

function tlpStyle(tlp: string) {
  if (tlp?.includes('RED'))   return 'text-red-400 border-red-700/50 bg-red-950/30'
  if (tlp?.includes('AMBER')) return 'text-amber-400 border-amber-700/50 bg-amber-950/30'
  if (tlp?.includes('GREEN')) return 'text-green-400 border-green-700/50 bg-green-950/30'
  return 'text-gray-400 border-gray-700/50 bg-gray-900/30'
}

// ── Primitive components ──────────────────────────────────────────────────────

function SycekMark() {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className="w-6 h-6 rounded bg-gradient-to-br from-purple-500 to-purple-900 flex items-center justify-center shadow-lg shadow-purple-900/50">
        <span className="text-white font-black text-xs font-mono">S</span>
      </div>
      <span className="text-white font-mono font-bold text-[10px] tracking-widest">SYCEK</span>
    </div>
  )
}

function TypeBadge({ type }: { type: string }) {
  const label = (type ?? '').replace(/_/g, ' ')
  const c =
    type?.includes('EMAIL')    ? 'text-blue-400 bg-blue-950/60 border-blue-800/40' :
    type?.includes('PHONE')    ? 'text-cyan-400 bg-cyan-950/60 border-cyan-800/40' :
    type?.includes('NAME')     ? 'text-purple-400 bg-purple-950/60 border-purple-800/40' :
    type?.includes('ADDRESS')  ? 'text-orange-400 bg-orange-950/60 border-orange-800/40' :
    type?.includes('USERNAME') ? 'text-teal-400 bg-teal-950/60 border-teal-800/40' :
    type?.includes('IP')       ? 'text-red-400 bg-red-950/60 border-red-800/40' :
    type?.includes('DOMAIN')   ? 'text-indigo-400 bg-indigo-950/60 border-indigo-800/40' :
    type?.includes('PASSWORD') ? 'text-rose-400 bg-rose-950/60 border-rose-800/40' :
    type?.includes('SOCIAL')   ? 'text-sky-400 bg-sky-950/60 border-sky-800/40' :
    type?.includes('DEVICE')   ? 'text-yellow-400 bg-yellow-950/60 border-yellow-800/40' :
    'text-gray-400 bg-gray-800/60 border-gray-700/40'
  return (
    <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded border uppercase tracking-wide leading-none whitespace-nowrap ${c}`}>
      {label}
    </span>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-purple-500 font-mono text-[10px] font-bold tracking-widest uppercase mb-3 pb-1.5 border-b border-purple-900/25 flex items-center gap-2">
      <div className="w-1 h-3 rounded-full bg-purple-600" />
      {children}
    </div>
  )
}

function Empty({ msg }: { msg: string }) {
  return <div className="text-gray-700 font-mono text-xs py-5 text-center border border-dashed border-gray-800 rounded-lg">{msg}</div>
}

function MiniBar({ score }: { score: number }) {
  const pct = Math.round(score * 100)
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1 bg-gray-800 rounded-full overflow-hidden shrink-0">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: scoreColor(pct) }} />
      </div>
      <span className="font-mono text-[10px] font-bold" style={{ color: scoreColor(pct) }}>{pct}%</span>
    </div>
  )
}

// ── Confidence arc (SVG semicircle) ───────────────────────────────────────────

function ConfidenceArc({ score }: { score: number }) {
  const pct = Math.round(score * 100)
  const r = 38
  const half = Math.PI * r
  const fill = (pct / 100) * half
  const col = scoreColor(pct)
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="100" height="62" viewBox="0 0 100 62">
        <defs>
          <filter id="glow-arc" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.5" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <path d="M 12 56 A 38 38 0 0 1 88 56" fill="none" stroke="#1f2937" strokeWidth="8" strokeLinecap="round" />
        <path d="M 12 56 A 38 38 0 0 1 88 56" fill="none" stroke={col} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${fill} ${half}`} filter="url(#glow-arc)"
          style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)' }} />
        <text x="50" y="50" textAnchor="middle" fill={col} fontSize="19" fontWeight="900" fontFamily="monospace">{pct}%</text>
      </svg>
      <div className="font-mono text-[9px] text-gray-600 tracking-wider">ATTRIBUTION CONFIDENCE</div>
    </div>
  )
}

// ── Stage row ────────────────────────────────────────────────────────────────

function StageRow({ def, event }: { def: typeof STAGE_DEFS[0]; event?: StageEvent }) {
  const status = event?.status ?? 'pending'
  const cfg = {
    running: { ring: 'border-yellow-500 text-yellow-400', badge: 'bg-yellow-950/60 text-yellow-400 border-yellow-800/50', label: 'LIVE', row: 'bg-yellow-950/10 border border-yellow-900/25' },
    complete: { ring: 'border-green-600 text-green-400', badge: 'bg-green-950/40 text-green-400 border-green-800/40', label: 'DONE', row: 'bg-green-950/6 border border-green-900/15' },
    error:    { ring: 'border-red-600 text-red-400', badge: 'bg-red-950/40 text-red-400 border-red-800/40', label: 'ERR', row: 'bg-red-950/10 border border-red-900/20' },
    skipped:  { ring: 'border-gray-700 text-gray-500', badge: 'bg-gray-900/40 text-gray-500 border-gray-700/40', label: 'SKIP', row: 'border border-transparent' },
    pending:  { ring: 'border-gray-800 text-gray-700', badge: 'bg-transparent text-gray-700 border-gray-800/40', label: '—', row: 'border border-transparent' },
  }[status]

  return (
    <div className={`flex items-center gap-3 px-3 py-1.5 rounded-lg transition-all ${cfg.row}`}>
      <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${cfg.ring}`}>
        {status === 'running'
          ? <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
          : <span className="font-mono text-[9px] font-bold">{def.stage}</span>
        }
      </div>
      <div className="flex-1 min-w-0">
        <span className={`font-mono text-xs ${status === 'pending' ? 'text-gray-700' : 'text-gray-300'}`}>
          {def.name}
        </span>
        {status === 'complete' && event && (
          <span className="ml-2 font-mono text-xs text-gray-600">
            {event.node_count    != null && `${event.node_count} nodes`}
            {event.attribute_count != null && ` · ${event.attribute_count} attrs`}
            {event.confidence    != null && ` · ${Math.round(event.confidence * 100)}% conf`}
            {event.pivots_run    != null && ` · ${event.pivots_run} pivots`}
            {event.phone_reassigned       && ` · phone reassigned`}
            {event.contradictions_flagged > 0 && ` · ${event.contradictions_flagged} contradictions`}
          </span>
        )}
        {status === 'error' && event?.error && (
          <span className="ml-2 font-mono text-xs text-red-500">{event.error}</span>
        )}
      </div>
      <span className={`font-mono text-[9px] font-bold px-1.5 py-0.5 rounded border ${cfg.badge}`}>{cfg.label}</span>
    </div>
  )
}

// ── Section renderers ─────────────────────────────────────────────────────────

function TabOverview({ result }: { result: any }) {
  const report = result?.report
  const s1  = report?.section_1_subject_overview
  const s2  = report?.section_2_seed_analysis
  const s10 = report?.section_10_confidence_summary
  const ab  = s10?.attribute_breakdown ?? {}
  const gaps    = s10?.data_gaps ?? []
  const actions = s10?.resolution_actions ?? []

  return (
    <div className="space-y-6">

      {/* Subject + seed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-black/50 border border-purple-900/25 rounded-xl p-4 space-y-3">
          <SectionLabel>Subject Overview</SectionLabel>
          {s1?.primary_name_variants?.length > 0 ? (
            <div className="space-y-1">
              {s1.primary_name_variants.map((n: string, i: number) => (
                <div key={i} className={`font-mono font-bold ${i === 0 ? 'text-white text-lg' : 'text-gray-500 text-sm'}`}>{n}</div>
              ))}
            </div>
          ) : (
            <div className="text-gray-600 font-mono text-sm">No name variants identified</div>
          )}
          <div className="text-purple-400 font-mono text-xs font-bold">{result.confidence_label}</div>
          <div className="text-gray-600 font-mono text-xs leading-relaxed border-t border-purple-900/20 pt-3">
            {s1?.attribution_basis}
          </div>
          {s1?.secondary_clusters_detected > 0 && (
            <div className="px-3 py-2 bg-yellow-950/20 border border-yellow-900/30 rounded-lg font-mono text-xs text-yellow-600">
              {s1.secondary_clusters_detected} secondary cluster{s1.secondary_clusters_detected > 1 ? 's' : ''} detected — see Threats & Network tab
            </div>
          )}
        </div>

        <div className="bg-black/50 border border-purple-900/25 rounded-xl p-4 space-y-3">
          <SectionLabel>Seed Analysis</SectionLabel>
          <table className="w-full font-mono text-xs">
            <tbody>
              {([
                ['Seed value',    <span className="text-white font-bold">{s2?.seed_value}</span>],
                ['Type',          <TypeBadge type={(s2?.seed_type ?? '').toUpperCase()} />],
                ['Temporal',      <span className={`font-bold ${s2?.temporal_status === 'reassigned' ? 'text-orange-400' : s2?.temporal_status === 'active' ? 'text-green-400' : 'text-gray-500'}`}>{(s2?.temporal_status ?? '—').toUpperCase()}</span>],
                s2?.cnam_name ? ['CNAM registered', <span className="text-cyan-400">{s2.cnam_name}</span>] : null,
                ['Currently active', <span className={s2?.current_valid ? 'text-green-400 font-bold' : 'text-gray-600'}>{s2?.current_valid === true ? 'YES' : s2?.current_valid === false ? 'NO' : '—'}</span>],
              ] as any[]).filter(Boolean).map(([k, v]: any, i: number) => (
                <tr key={i}>
                  <td className="text-gray-600 pr-4 pb-2 whitespace-nowrap">{k}</td>
                  <td className="pb-2">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {s2?.phone_reassigned && (
            <div className="p-3 bg-orange-950/25 border border-orange-800/40 rounded-lg text-orange-400 font-mono text-xs leading-relaxed">
              {s2.reassignment_note}
            </div>
          )}
        </div>
      </div>

      {/* Confidence breakdown */}
      <div>
        <SectionLabel>Evidence Quality Breakdown</SectionLabel>
        <div className="grid grid-cols-3 gap-3">
          {[
            { k: 'high_confidence',     label: 'HIGH CONFIDENCE',  color: 'text-green-400 border-green-800/40 bg-green-950/15' },
            { k: 'moderate_confidence', label: 'MODERATE',         color: 'text-yellow-400 border-yellow-800/40 bg-yellow-950/15' },
            { k: 'low_confidence',      label: 'LOW CONFIDENCE',   color: 'text-red-400 border-red-800/40 bg-red-950/15' },
          ].map(({ k, label, color }) => (
            <div key={k} className={`border rounded-xl p-4 text-center ${color}`}>
              <div className="font-mono text-3xl font-black">{ab[k] ?? 0}</div>
              <div className="font-mono text-[9px] mt-1 opacity-60 tracking-widest">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Data gaps */}
      {gaps.length > 0 && (
        <div>
          <SectionLabel>Data Gaps & Recommended Actions</SectionLabel>
          <div className="space-y-2">
            {gaps.map((gap: string, i: number) => (
              <div key={i} className="flex gap-3 items-start p-3 bg-black/40 border border-gray-800/40 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-600 mt-1.5 shrink-0" />
                <div>
                  <div className="text-yellow-600 font-mono text-[10px] font-bold tracking-wider">{gap.replace(/_/g, ' ').toUpperCase()}</div>
                  {actions[i] && <div className="text-gray-500 font-mono text-xs mt-0.5 leading-relaxed">{actions[i]}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Methodology */}
      <div className="p-4 bg-purple-950/10 border border-purple-900/20 rounded-xl">
        <div className="text-purple-600 font-mono text-[9px] font-bold tracking-widest mb-2">ANALYTICAL METHODOLOGY</div>
        <div className="text-gray-600 font-mono text-xs leading-relaxed">
          {report?.section_9_attribution_evidence?.methodology}
        </div>
      </div>
    </div>
  )
}

function TabIdentityContact({ result }: { result: any }) {
  const report = result?.report
  const s3 = report?.section_3_identity_attributes
  const s4 = report?.section_4_contact_intelligence
  const attrs = s3?.table ?? []

  return (
    <div className="space-y-6">

      {/* Identity attributes */}
      <div>
        <SectionLabel>Identity Attributes — {attrs.length} found</SectionLabel>
        {attrs.length === 0 ? <Empty msg="No identity attributes extracted" /> : (
          <div className="overflow-x-auto rounded-xl border border-gray-800/50">
            <table className="w-full font-mono text-xs">
              <thead>
                <tr className="bg-gray-900/60 border-b border-gray-800">
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Attribute</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Type</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal w-28">Confidence</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">First</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Last</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Srcs</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Flag</th>
                </tr>
              </thead>
              <tbody>
                {attrs.map((row: any, i: number) => (
                  <tr key={i} className={`border-b border-gray-900/60 hover:bg-white/2 transition-colors ${row.contradiction ? 'bg-red-950/10' : ''}`}>
                    <td className="px-3 py-2 text-white font-bold max-w-44 truncate" title={row.attribute}>{row.attribute}</td>
                    <td className="px-3 py-2"><TypeBadge type={row.type} /></td>
                    <td className="px-3 py-2"><MiniBar score={row.confidence ?? 0} /></td>
                    <td className="px-3 py-2 text-gray-600">{row.first_seen ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{row.last_seen ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-500">{row.corroborated_by}x</td>
                    <td className="px-3 py-2">
                      {row.contradiction && (
                        <span className="font-mono text-[9px] font-bold text-red-400 bg-red-950/40 border border-red-800/40 px-1.5 py-0.5 rounded">CONFLICT</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Emails */}
      <div>
        <SectionLabel>Email Addresses — {s4?.email_addresses?.length ?? 0} found</SectionLabel>
        {!s4?.email_addresses?.length ? <Empty msg="No email addresses found" /> : (
          <div className="space-y-2">
            {s4.email_addresses.map((e: any, i: number) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 bg-black/40 border border-blue-900/20 rounded-xl hover:border-blue-800/35 transition-colors">
                <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                <div className="flex-1 font-mono text-sm text-white font-bold min-w-0 truncate">{e.value}</div>
                <TypeBadge type={e.type} />
                <div className="text-gray-600 font-mono text-xs whitespace-nowrap">{e.breach_exposure}x breaches</div>
                <div className="text-gray-700 font-mono text-xs whitespace-nowrap">{e.last_seen ?? '—'}</div>
                <div className={`font-mono text-xs font-bold whitespace-nowrap ${(e.uniqueness ?? 0) >= 0.8 ? 'text-purple-400' : 'text-gray-600'}`}>
                  {Math.round((e.uniqueness ?? 0) * 100)}% unique
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Phones */}
      <div>
        <SectionLabel>Phone Numbers — {s4?.phone_numbers?.length ?? 0} found</SectionLabel>
        {!s4?.phone_numbers?.length ? <Empty msg="No phone numbers found" /> : (
          <div className="space-y-2">
            {s4.phone_numbers.map((p: any, i: number) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-3 border rounded-xl ${p.reassigned ? 'bg-orange-950/10 border-orange-900/35' : 'bg-black/40 border-cyan-900/20'}`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${p.reassigned ? 'bg-orange-400' : p.current_valid ? 'bg-green-500' : 'bg-gray-600'}`} />
                <div className="flex-1 font-mono text-sm text-white font-bold">{p.value}</div>
                <TypeBadge type={p.type} />
                {p.cnam    && <div className="text-cyan-400 font-mono text-xs">{p.cnam}</div>}
                {p.carrier && <div className="text-gray-600 font-mono text-xs">{p.carrier}</div>}
                <div className="text-gray-700 font-mono text-xs">{p.last_seen ?? '—'}</div>
                {p.reassigned
                  ? <span className="font-mono text-[9px] font-bold text-orange-400 bg-orange-950/40 border border-orange-800/40 px-1.5 py-0.5 rounded whitespace-nowrap">REASSIGNED</span>
                  : <span className={`font-mono text-[9px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${p.current_valid ? 'text-green-400 bg-green-950/30 border-green-800/40' : 'text-gray-600 bg-gray-900/30 border-gray-800/30'}`}>{p.current_valid ? 'ACTIVE' : 'HISTORICAL'}</span>
                }
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Addresses */}
      <div>
        <SectionLabel>Physical Addresses — {s4?.physical_addresses?.length ?? 0} found</SectionLabel>
        {!s4?.physical_addresses?.length ? <Empty msg="No physical addresses found" /> : (
          <div className="space-y-2">
            {s4.physical_addresses.map((a: any, i: number) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 bg-black/40 border border-orange-900/20 rounded-xl">
                <div className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                <div className="flex-1 font-mono text-sm text-white">{a.value}</div>
                <div className="text-gray-600 font-mono text-xs whitespace-nowrap">{a.first_seen ?? '?'}–{a.last_seen ?? '?'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DOB + Govt IDs */}
      {((s4?.date_of_birth?.length ?? 0) > 0 || (s4?.government_ids?.length ?? 0) > 0) && (
        <div className="grid grid-cols-2 gap-4">
          {s4?.date_of_birth?.length > 0 && (
            <div>
              <SectionLabel>Date of Birth</SectionLabel>
              <div className="space-y-1">
                {s4.date_of_birth.map((d: string, i: number) => (
                  <div key={i} className="font-mono text-white font-bold px-4 py-2.5 bg-black/40 border border-purple-900/20 rounded-lg">{d}</div>
                ))}
              </div>
            </div>
          )}
          {s4?.government_ids?.length > 0 && (
            <div>
              <SectionLabel>Government IDs</SectionLabel>
              <div className="space-y-1">
                {s4.government_ids.map((d: string, i: number) => (
                  <div key={i} className="font-mono text-amber-400 font-bold px-4 py-2.5 bg-amber-950/15 border border-amber-900/35 rounded-lg">{d}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PivotBadge({ pivotKey }: { pivotKey: string }) {
  const [type, target] = pivotKey.split(/:(.+)/)
  const cfg: Record<string, { color: string; label: string }> = {
    breach:  { color: 'text-red-400 bg-red-950/40 border-red-800/40',     label: 'BREACH' },
    gmail:   { color: 'text-blue-400 bg-blue-950/40 border-blue-800/40',  label: 'GMAIL' },
    eyecon:  { color: 'text-cyan-400 bg-cyan-950/40 border-cyan-800/40',  label: 'CALLER ID' },
    whois:   { color: 'text-indigo-400 bg-indigo-950/40 border-indigo-800/40', label: 'WHOIS' },
  }
  const c = cfg[type] ?? { color: 'text-gray-400 bg-gray-800/40 border-gray-700/40', label: type.toUpperCase() }
  return (
    <div className="flex items-center gap-2 mb-1">
      <span className={`font-mono text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase ${c.color}`}>{c.label}</span>
      <span className="font-mono text-xs text-gray-400 truncate">{target}</span>
    </div>
  )
}

function PivotResultCard({ pivotKey, data }: { pivotKey: string; data: any }) {
  const [type] = pivotKey.split(':')

  if (data?.error) {
    return (
      <div className="p-3 bg-black/30 border border-gray-800/50 rounded-xl">
        <PivotBadge pivotKey={pivotKey} />
        <div className="text-red-500 font-mono text-xs mt-1">{data.error === 'timeout' ? 'Pivot timed out' : data.error}</div>
      </div>
    )
  }

  if (type === 'breach') {
    const nodes = data?.nodes ?? []
    const valueNodes = nodes.filter((n: any) => n.type !== 'identity' && n.props)
    return (
      <div className="p-3 bg-black/30 border border-red-900/25 rounded-xl">
        <PivotBadge pivotKey={pivotKey} />
        <div className="text-gray-500 font-mono text-xs mb-2">{data?.node_count ?? 0} nodes · {data?.edge_count ?? 0} edges found</div>
        {valueNodes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {valueNodes.slice(0, 12).map((n: any, i: number) => (
              <span key={i} className="font-mono text-[10px] text-gray-300 bg-gray-900/60 border border-gray-800/50 px-2 py-0.5 rounded truncate max-w-[180px]" title={n.label}>{n.label}</span>
            ))}
            {valueNodes.length > 12 && <span className="font-mono text-[10px] text-gray-600">+{valueNodes.length - 12} more</span>}
          </div>
        )}
      </div>
    )
  }

  if (type === 'gmail') {
    const pic  = data?.profile_picture
    const cover = data?.cover_picture
    const hasData = pic || data?.gaia_id || data?.entity_type || data?.activated_services?.length
    return (
      <div className="bg-black/30 border border-blue-900/25 rounded-xl overflow-hidden">
        {/* Cover banner */}
        {cover && (
          <img src={cover} alt="" className="w-full h-16 object-cover opacity-60" crossOrigin="anonymous" referrerPolicy="no-referrer" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
        )}
        <div className="p-3 space-y-2">
          <PivotBadge pivotKey={pivotKey} />
          {/* Profile header */}
          <div className="flex items-center gap-3">
            {pic ? (
              <img src={pic} alt="" className="w-10 h-10 rounded-full border border-blue-700/50 shrink-0 object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
            ) : (
              <div className="w-10 h-10 rounded-full bg-blue-950/60 border border-blue-800/40 flex items-center justify-center shrink-0">
                <span className="text-blue-400 font-mono text-sm">G</span>
              </div>
            )}
            <div>
              {data?.entity_type && <div className="font-mono text-xs text-blue-300 font-bold">{data.entity_type}</div>}
              {data?.enterprise_user !== undefined && (
                <div className={`font-mono text-[9px] px-1.5 py-0.5 rounded border inline-block ${data.enterprise_user ? 'text-purple-400 bg-purple-950/40 border-purple-800/40' : 'text-gray-600 border-gray-800/40'}`}>
                  {data.enterprise_user ? 'ENTERPRISE' : 'CONSUMER'}
                </div>
              )}
            </div>
          </div>
          {/* Metadata table */}
          {(data?.gaia_id || data?.customer_id || data?.last_profile_edit) && (
            <table className="w-full font-mono text-xs">
              <tbody>
                {data.gaia_id        && <tr><td className="text-gray-600 pr-3 pb-1 whitespace-nowrap">GAIA ID</td><td className="text-gray-300 pb-1 break-all">{data.gaia_id}</td></tr>}
                {data.customer_id    && <tr><td className="text-gray-600 pr-3 pb-1 whitespace-nowrap">Customer ID</td><td className="text-gray-300 pb-1">{data.customer_id}</td></tr>}
                {data.last_profile_edit && <tr><td className="text-gray-600 pr-3 pb-1 whitespace-nowrap">Last edit</td><td className="text-gray-400 pb-1">{data.last_profile_edit}</td></tr>}
              </tbody>
            </table>
          )}
          {/* User types */}
          {data?.user_types?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {data.user_types.map((t: string, i: number) => (
                <span key={i} className="font-mono text-[9px] px-1.5 py-0.5 rounded border text-blue-400 bg-blue-950/30 border-blue-800/40">{t}</span>
              ))}
            </div>
          )}
          {/* Activated services */}
          {data?.activated_services?.length > 0 && (
            <div>
              <div className="text-gray-600 font-mono text-[9px] tracking-widest mb-1">ACTIVATED SERVICES</div>
              <div className="flex flex-wrap gap-1">
                {data.activated_services.map((s: string, i: number) => (
                  <span key={i} className="font-mono text-[9px] px-1.5 py-0.5 rounded border text-green-400 bg-green-950/20 border-green-800/30">{s}</span>
                ))}
              </div>
            </div>
          )}
          {/* Profile links */}
          {[
            { key: 'maps_profile', label: 'Maps' },
            { key: 'play_games',   label: 'Play Games' },
            { key: 'calendar',     label: 'Calendar' },
          ].filter(l => data?.[l.key]).map(l => (
            <a key={l.key} href={data[l.key]} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-mono text-[10px] text-blue-400 hover:text-blue-200 truncate transition-colors">
              <span className="text-gray-600">→</span> {l.label}: {data[l.key]}
            </a>
          ))}
          {!hasData && <div className="font-mono text-xs text-gray-600">No Gmail profile found</div>}
        </div>
      </div>
    )
  }

  if (type === 'eyecon') {
    const name = data?.name || data?.cnam || data?.caller_name
    const carrier = data?.carrier || data?.network
    const fb = data?.facebook_profile || data?.social?.facebook
    return (
      <div className="p-3 bg-black/30 border border-cyan-900/25 rounded-xl">
        <PivotBadge pivotKey={pivotKey} />
        {name    && <div className="font-mono text-sm text-white font-bold">{name}</div>}
        {carrier && <div className="font-mono text-xs text-cyan-400 mt-0.5">{carrier}</div>}
        {fb      && <div className="font-mono text-xs text-blue-400 mt-0.5 truncate">{typeof fb === 'string' ? fb : JSON.stringify(fb)}</div>}
        {!name && !carrier && <div className="font-mono text-xs text-gray-600">No caller ID data found</div>}
      </div>
    )
  }

  if (type === 'whois') {
    const registrar = data?.registrar || data?.Registrar
    const created   = data?.creation_date || data?.['Creation Date']
    const expires   = data?.expiration_date || data?.['Expiration Date']
    const registrant = data?.registrant_name || data?.['Registrant Name']
    const email     = data?.registrant_email || data?.emails?.[0]
    return (
      <div className="p-3 bg-black/30 border border-indigo-900/25 rounded-xl">
        <PivotBadge pivotKey={pivotKey} />
        <table className="w-full font-mono text-xs mt-1">
          <tbody>
            {registrant && <tr><td className="text-gray-600 pr-3 pb-1">Registrant</td><td className="text-white font-bold pb-1">{registrant}</td></tr>}
            {email      && <tr><td className="text-gray-600 pr-3 pb-1">Email</td><td className="text-blue-300 pb-1">{email}</td></tr>}
            {registrar  && <tr><td className="text-gray-600 pr-3 pb-1">Registrar</td><td className="text-gray-400 pb-1">{registrar}</td></tr>}
            {created    && <tr><td className="text-gray-600 pr-3 pb-1">Created</td><td className="text-gray-400 pb-1">{typeof created === 'string' ? created.slice(0, 10) : String(created).slice(0, 10)}</td></tr>}
            {expires    && <tr><td className="text-gray-600 pr-3 pb-1">Expires</td><td className="text-gray-400 pb-1">{typeof expires === 'string' ? expires.slice(0, 10) : String(expires).slice(0, 10)}</td></tr>}
          </tbody>
        </table>
        {!registrant && !email && !registrar && <div className="font-mono text-xs text-gray-600">No WHOIS data found</div>}
      </div>
    )
  }

  // Generic fallback
  const entries = Object.entries(data ?? {}).filter(([, v]) => v != null && v !== '' && typeof v !== 'object')
  return (
    <div className="p-3 bg-black/30 border border-gray-800/50 rounded-xl">
      <PivotBadge pivotKey={pivotKey} />
      {entries.length > 0 ? (
        <div className="space-y-0.5 mt-1">
          {entries.slice(0, 8).map(([k, v]) => (
            <div key={k} className="flex gap-2 font-mono text-xs">
              <span className="text-gray-600 shrink-0">{k}:</span>
              <span className="text-gray-300 truncate">{String(v)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="font-mono text-xs text-gray-600 mt-1">No data returned</div>
      )}
    </div>
  )
}

function TabDigitalCorporate({ result }: { result: any }) {
  const report = result?.report
  const s5 = report?.section_5_digital_footprint
  const s6 = report?.section_6_corporate_financial
  const pivotResults: Record<string, any> = result?.pivot_results ?? {}

  return (
    <div className="space-y-6">

      {/* Usernames */}
      <div>
        <SectionLabel>Usernames — {s5?.usernames?.length ?? 0} found</SectionLabel>
        {!s5?.usernames?.length ? <Empty msg="No usernames found" /> : (
          <div className="grid grid-cols-2 gap-2">
            {s5.usernames.map((u: any, i: number) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-black/40 border border-teal-900/20 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                <div className="flex-1 font-mono text-sm text-teal-300 font-bold truncate">{u.value}</div>
                <div className={`font-mono text-[10px] font-bold ${(u.uniqueness ?? 0) >= 0.8 ? 'text-purple-400' : 'text-gray-600'}`}>
                  {Math.round((u.uniqueness ?? 0) * 100)}%
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Social profiles */}
      <div>
        <SectionLabel>Social Profiles — {s5?.social_profiles?.length ?? 0} found</SectionLabel>
        {!s5?.social_profiles?.length ? <Empty msg="No social profiles found" /> : (
          <div className="space-y-1.5">
            {s5.social_profiles.map((p: any, i: number) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 bg-black/40 border border-sky-900/20 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />
                <div className="font-mono text-xs text-sky-300 break-all">{p.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* IP addresses */}
      <div>
        <SectionLabel>IP Addresses — {s5?.ip_addresses?.length ?? 0} found</SectionLabel>
        {!s5?.ip_addresses?.length ? <Empty msg="No IP addresses found" /> : (
          <div className="overflow-x-auto rounded-xl border border-gray-800/50">
            <table className="w-full font-mono text-xs">
              <thead>
                <tr className="bg-gray-900/60 border-b border-gray-800">
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">IP Address</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Type</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {s5.ip_addresses.map((ip: any, i: number) => (
                  <tr key={i} className="border-b border-gray-900/50 hover:bg-white/2">
                    <td className="px-3 py-2 text-red-300 font-bold">{ip.value}</td>
                    <td className="px-3 py-2"><TypeBadge type={ip.type} /></td>
                    <td className="px-3 py-2 text-gray-600">{ip.last_seen ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Device IDs */}
      {(s5?.device_identifiers?.length ?? 0) > 0 && (
        <div>
          <SectionLabel>Device Identifiers — {s5.device_identifiers.length} found</SectionLabel>
          <div className="space-y-1.5">
            {s5.device_identifiers.map((d: string, i: number) => (
              <div key={i} className="font-mono text-xs text-yellow-400 px-3 py-2.5 bg-yellow-950/10 border border-yellow-900/25 rounded-lg">{d}</div>
            ))}
          </div>
        </div>
      )}

      {/* Employers */}
      <div>
        <SectionLabel>Employers — {s6?.employers?.length ?? 0} found</SectionLabel>
        {!s6?.employers?.length ? <Empty msg="No employer information found" /> : (
          <div className="flex flex-wrap gap-2">
            {s6.employers.map((e: string, i: number) => (
              <span key={i} className="font-mono text-sm text-purple-300 bg-purple-950/25 border border-purple-800/40 px-4 py-2 rounded-lg">{e}</span>
            ))}
          </div>
        )}
      </div>

      {/* Domains */}
      <div>
        <SectionLabel>Domains Registered — {s6?.domains_registered?.length ?? 0} found</SectionLabel>
        {!s6?.domains_registered?.length ? <Empty msg="No domains found" /> : (
          <div className="grid grid-cols-2 gap-2">
            {s6.domains_registered.map((d: any, i: number) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-black/40 border border-indigo-900/20 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                <div className="flex-1 font-mono text-sm text-indigo-300 font-bold truncate">{d.value}</div>
                <div className="text-gray-700 font-mono text-xs">{d.first_seen ?? '—'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Password exposure */}
      {(s6?.password_exposure?.count ?? 0) > 0 && (
        <div className="p-4 bg-rose-950/20 border border-rose-900/40 rounded-xl">
          <div className="flex items-center justify-between mb-2">
            <div className="text-rose-400 font-mono text-xs font-bold tracking-wider">PASSWORD EXPOSURE</div>
            <div className="text-rose-300 font-mono text-2xl font-black">{s6.password_exposure.count}</div>
          </div>
          <div className="text-rose-700 font-mono text-xs mb-2">credentials found in breach databases</div>
          <div className="flex flex-wrap gap-2">
            {(s6.password_exposure.types ?? []).map((t: string, i: number) => (
              <TypeBadge key={i} type={t} />
            ))}
          </div>
        </div>
      )}

      {/* Pivot enrichment results */}
      {Object.keys(pivotResults).length > 0 && (
        <div>
          <SectionLabel>Pivot Enrichment Results — {Object.keys(pivotResults).length} pivots run</SectionLabel>
          <div className="space-y-2">
            {Object.entries(pivotResults).map(([key, data]) => (
              <PivotResultCard key={key} pivotKey={key} data={data} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function TabThreatsNetwork({ result }: { result: any }) {
  const report = result?.report
  const s7 = report?.section_7_threat_indicators
  const s8 = report?.section_8_network_connections

  return (
    <div className="space-y-6">

      {/* Threat metric cards */}
      <div>
        <SectionLabel>Threat Indicators</SectionLabel>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: 'Breach Sources',      val: s7?.breach_exposure_count ?? 0, color: 'text-purple-400 border-purple-800/40 bg-purple-950/20' },
            { label: 'Credentials Exposed', val: s7?.credentials_exposed ?? 0,   color: 'text-rose-400 border-rose-800/40 bg-rose-950/20' },
            { label: 'Govt ID Fragments',   val: s7?.government_id_fragments ?? 0, color: 'text-amber-400 border-amber-800/40 bg-amber-950/20' },
          ].map(({ label, val, color }) => (
            <div key={label} className={`border rounded-xl p-4 text-center ${color}`}>
              <div className="font-mono text-3xl font-black">{val}</div>
              <div className="font-mono text-[9px] mt-1 opacity-60 tracking-widest uppercase">{label}</div>
            </div>
          ))}
        </div>

        {s7?.stealer_logs_detected && (
          <div className="relative p-4 bg-red-950/30 border border-red-700/50 rounded-xl overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-600" />
            <div className="pl-2">
              <div className="text-red-400 font-mono text-xs font-bold tracking-wide mb-1">
                STEALER LOG DETECTED
              </div>
              <div className="text-red-600 font-mono text-xs leading-relaxed">
                Credential-harvesting malware was previously active on a device associated with this identity.
                Device identifiers, session cookies, and stored credentials may have been exfiltrated.
                Immediate full credential rotation recommended. Do not rely on CNAM for current attribution.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Secondary clusters */}
      <div>
        <SectionLabel>Secondary Identity Clusters — {s8?.secondary_clusters?.length ?? 0} detected</SectionLabel>
        {!s8?.secondary_clusters?.length ? (
          <Empty msg="No secondary identity clusters detected" />
        ) : (
          <div className="space-y-3">
            {s8.secondary_clusters.map((c: any, i: number) => (
              <div key={i} className="p-4 bg-black/40 border border-yellow-900/30 rounded-xl">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-yellow-500 font-mono text-[10px] font-bold tracking-wider">CLUSTER {(c.cluster_id ?? '').slice(-8).toUpperCase()}</div>
                    {c.name_variants?.length > 0 && (
                      <div className="text-white font-mono text-base font-black mt-0.5">
                        {c.name_variants.join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-yellow-400 font-mono text-lg font-black">{Math.round((c.confidence ?? 0) * 100)}%</div>
                    <div className="text-gray-600 font-mono text-xs">{c.attribute_count} attributes</div>
                  </div>
                </div>
                <div className="text-gray-600 font-mono text-xs leading-relaxed border-t border-gray-800/40 pt-2">{c.relationship}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function TabEvidence({ result }: { result: any }) {
  const s9 = result?.report?.section_9_attribution_evidence
  const evidence = s9?.evidence ?? []

  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>Attribution Evidence Chain — {evidence.length} items</SectionLabel>
        {!evidence.length ? <Empty msg="No evidence items in attribution chain" /> : (
          <div className="overflow-x-auto rounded-xl border border-gray-800/50">
            <table className="w-full font-mono text-xs">
              <thead>
                <tr className="bg-gray-900/60 border-b border-gray-800">
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal w-6">#</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Attribute</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Type</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal w-24">Score</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Corroboration</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Sources</th>
                  <th className="text-left px-3 py-2.5 text-gray-500 font-normal">Note</th>
                </tr>
              </thead>
              <tbody>
                {evidence.map((e: any, i: number) => (
                  <tr key={i} className="border-b border-gray-900/60 hover:bg-white/2 transition-colors">
                    <td className="px-3 py-2.5 text-gray-700">{e.rank ?? i + 1}</td>
                    <td className="px-3 py-2.5 text-white font-bold max-w-36 truncate" title={e.attribute}>{e.attribute}</td>
                    <td className="px-3 py-2.5"><TypeBadge type={e.type} /></td>
                    <td className="px-3 py-2.5"><MiniBar score={e.score ?? 0} /></td>
                    <td className="px-3 py-2.5 text-gray-400">{e.corroboration}x</td>
                    <td className="px-3 py-2.5 text-gray-600 max-w-28 truncate" title={(e.sources ?? []).join(', ')}>
                      {(e.sources ?? []).slice(0, 2).join(', ')}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 max-w-44 truncate" title={e.note}>{e.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="p-4 bg-purple-950/10 border border-purple-900/20 rounded-xl">
        <div className="text-purple-600 font-mono text-[9px] font-bold tracking-widest mb-2">METHODOLOGY</div>
        <div className="text-gray-500 font-mono text-xs leading-relaxed">{s9?.methodology}</div>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function BTRAInvestigationPanel({ query }: BTRAInvestigationPanelProps) {
  const [state, setState] = useState<PipelineState>({
    status: 'idle', stages: [], result: null, error: null, currentStage: 0,
  })
  const [activeTab, setActiveTab] = useState('overview')
  const [pipelineOpen, setPipelineOpen] = useState(true)
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null)
  const bufferRef = useRef('')

  const handleEvent = useCallback((event: any) => {
    switch (event.event) {
      case 'pipeline_start':
        setState(s => ({ ...s, currentStage: 1 }))
        break
      case 'stage':
        setState(s => {
          const stages = [...s.stages]
          const idx = stages.findIndex(st => st.stage === event.stage && st.name === event.name)
          idx >= 0 ? (stages[idx] = event) : stages.push(event)
          return { ...s, stages, currentStage: event.status === 'running' ? event.stage : s.currentStage }
        })
        break
      case 'complete':
        setState(s => ({ ...s, status: 'complete', result: event.result }))
        setPipelineOpen(false)
        break
      case 'error':
        setState(s => ({ ...s, status: 'error', error: event.message }))
        break
    }
  }, [])

  const startInvestigation = useCallback(async () => {
    if (readerRef.current) { await readerRef.current.cancel(); readerRef.current = null }
    setState({ status: 'running', stages: [], result: null, error: null, currentStage: 1 })
    setPipelineOpen(true)
    bufferRef.current = ''

    const token = getToken()
    const url = `/api/btra/investigate?query=${encodeURIComponent(query)}&token=${encodeURIComponent(token)}`

    try {
      const res = await fetch(url, { headers: { Accept: 'text/event-stream' }, cache: 'no-store' })
      if (!res.ok) {
        const body = await res.text()
        setState(s => ({ ...s, status: 'error', error: `HTTP ${res.status}: ${body}` }))
        return
      }
      const reader = res.body!.getReader()
      readerRef.current = reader
      const dec = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        bufferRef.current += dec.decode(value, { stream: true })
        const lines = bufferRef.current.split('\n')
        bufferRef.current = lines.pop() ?? ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try { handleEvent(JSON.parse(line.slice(6))) } catch { /* ignore malformed */ }
        }
      }
    } catch (err: any) {
      setState(s => ({ ...s, status: 'error', error: err.message ?? 'Connection failed' }))
    }
  }, [query, handleEvent])

  const stop = useCallback(async () => {
    if (readerRef.current) { await readerRef.current.cancel(); readerRef.current = null }
    setState(s => ({ ...s, status: 'idle' }))
  }, [])

  const getStageEvent = (n: number) => state.stages.find(s => s.stage === n)

  const result  = state.result
  const report  = result?.report
  const meta    = report?.metadata
  const s1      = report?.section_1_subject_overview
  const s7      = report?.section_7_threat_indicators
  const tlp     = meta?.classification ?? 'TLP:WHITE'
  const conf    = result?.attribution_confidence ?? 0
  const primaryName = s1?.primary_name_variants?.[0] ?? query

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-2xl border border-purple-900/50 overflow-hidden"
      style={{ background: '#07070f', boxShadow: '0 0 60px rgba(88,28,235,0.07), 0 1px 0 rgba(139,92,246,0.15) inset' }}
    >

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div
        className="relative border-b border-purple-900/50 px-5 py-4"
        style={{ background: 'linear-gradient(135deg, rgba(60,20,120,0.5) 0%, rgba(0,0,0,0.8) 50%, rgba(60,20,120,0.4) 100%)' }}
      >
        {/* Top shimmer line */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.6), transparent)' }} />

        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <SycekMark />
            <div className="h-8 w-px bg-purple-900/60 shrink-0" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-purple-400 font-mono text-[11px] tracking-widest font-bold uppercase">
                  Breach Threat & Risk Assessment
                </span>
                <span className="font-mono text-[9px] text-green-500 border border-green-900/60 bg-green-950/20 px-2 py-0.5 rounded-full tracking-wider">
                  LIVE DATA
                </span>
              </div>
              <div className="text-gray-200 font-mono text-sm font-bold mt-0.5 truncate">
                7-Stage Attribution Pipeline · <span className="text-purple-300">{query}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {state.status === 'complete' && meta && (
              <div className={`font-mono text-xs font-bold px-3 py-1.5 rounded-lg border ${tlpStyle(tlp)}`}>{tlp}</div>
            )}
            {state.status === 'running' ? (
              <button onClick={stop}
                className="px-4 py-2 bg-red-900/40 hover:bg-red-900/60 border border-red-800/50 text-red-300 font-mono text-xs rounded-xl transition-colors">
                STOP
              </button>
            ) : (
              <button onClick={startInvestigation}
                className="px-5 py-2 font-mono text-sm font-bold rounded-xl transition-all border border-purple-600/50 text-purple-100"
                style={{ background: 'linear-gradient(135deg, rgba(109,40,217,0.7), rgba(88,28,235,0.6))' }}>
                {state.status === 'idle' ? 'RUN INVESTIGATION' : state.status === 'complete' ? 'RE-RUN (FRESH DATA)' : 'START'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Pipeline stages (collapsible) ───────────────────────────────────── */}
      <div className="border-b border-purple-900/20">
        <button
          onClick={() => setPipelineOpen(o => !o)}
          className="w-full flex items-center justify-between px-5 py-2.5 hover:bg-white/2 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-purple-600 font-mono text-[10px] font-bold tracking-widest">PIPELINE STAGES</span>
            {state.status === 'running' && (
              <span className="flex items-center gap-1.5 text-yellow-500 font-mono text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                Stage {state.currentStage} of 7
              </span>
            )}
            {state.status === 'complete' && (
              <span className="text-green-700 font-mono text-[10px]">All stages complete</span>
            )}
          </div>
          <span className="text-gray-700 text-xs">{pipelineOpen ? '▲' : '▼'}</span>
        </button>
        <AnimatePresence initial={false}>
          {pipelineOpen && (
            <motion.div
              initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-5 pb-4 space-y-1">
                {STAGE_DEFS.map(def => (
                  <StageRow key={def.stage} def={def} event={getStageEvent(def.stage)} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Error banner ─────────────────────────────────────────────────────── */}
      {state.error && (
        <div className="px-5 py-3 bg-red-950/30 border-b border-red-900/40">
          <div className="text-red-400 font-mono text-xs">{state.error}</div>
        </div>
      )}

      {/* ── Idle state ──────────────────────────────────────────────────────── */}
      {state.status === 'idle' && (
        <div className="px-5 py-12 text-center space-y-2">
          <div className="text-gray-700 font-mono text-xs">
            Temporal deconfliction · Identity clustering · Diminishing-returns attribution scoring
          </div>
          <div className="text-gray-800 font-mono text-xs mt-2">
            Estimated runtime: 30–90 seconds · Results stream live as each stage completes
          </div>
        </div>
      )}

      {/* ── Results ─────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {state.status === 'complete' && result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>

            {/* Subject hero */}
            <div className="relative px-5 py-5 border-b border-purple-900/25"
              style={{ background: 'linear-gradient(180deg, rgba(60,20,120,0.15) 0%, transparent 100%)' }}>
              <div className="flex items-center gap-6">
                <div className="shrink-0"><ConfidenceArc score={conf} /></div>
                <div className="h-16 w-px bg-purple-900/40 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-mono text-xl font-black truncate">{primaryName}</div>
                  {s1?.primary_name_variants?.slice(1).map((n: string, i: number) => (
                    <div key={i} className="text-gray-600 font-mono text-sm">{n}</div>
                  ))}
                  <div className="text-purple-400 font-mono text-xs font-bold mt-1">{result.confidence_label}</div>
                  <div className="text-gray-600 font-mono text-xs mt-0.5 leading-relaxed truncate">{s1?.attribution_basis}</div>
                </div>
                <div className="shrink-0 grid grid-cols-2 gap-2">
                  {[
                    { v: s7?.breach_exposure_count ?? 0, l: 'BREACH SOURCES' },
                    { v: report?.section_3_identity_attributes?.table?.length ?? 0, l: 'ATTRIBUTES' },
                    { v: result.evidence_chain?.length ?? 0, l: 'EVIDENCE ITEMS' },
                    { v: result.secondary_clusters?.length ?? 0, l: 'CLUSTERS' },
                  ].map(({ v, l }) => (
                    <div key={l} className="text-center bg-black/60 border border-purple-900/25 rounded-xl px-4 py-3">
                      <div className="text-white font-mono text-xl font-black">{v}</div>
                      <div className="text-gray-600 font-mono text-[8px] tracking-widest mt-0.5">{l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alert strip */}
              {(s7?.stealer_logs_detected || result.temporal_status === 'reassigned') && (
                <div className="flex gap-2 mt-4">
                  {s7?.stealer_logs_detected && (
                    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-red-950/35 border border-red-700/50 rounded-xl">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" />
                      <div className="text-red-400 font-mono text-xs font-bold">STEALER LOG DETECTED — credential harvesting malware associated with this identity</div>
                    </div>
                  )}
                  {result.temporal_status === 'reassigned' && (
                    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-orange-950/25 border border-orange-700/40 rounded-xl">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                      <div className="text-orange-400 font-mono text-xs">Phone number reassigned — pivot via email/username attributes only</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tab nav */}
            <div className="border-b border-purple-900/25 px-5 flex gap-0 overflow-x-auto"
              style={{ background: 'rgba(7,7,15,0.8)' }}>
              {TABS.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`font-mono text-[10px] font-bold tracking-wider px-4 py-3.5 border-b-2 transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-300'
                      : 'border-transparent text-gray-600 hover:text-gray-400 hover:border-gray-700'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="px-5 py-6">
              {activeTab === 'overview'  && <TabOverview result={result} />}
              {activeTab === 'identity'  && <TabIdentityContact result={result} />}
              {activeTab === 'digital'   && <TabDigitalCorporate result={result} />}
              {activeTab === 'threats'   && <TabThreatsNetwork result={result} />}
              {activeTab === 'evidence'  && <TabEvidence result={result} />}
            </div>

            {/* Footer */}
            <div className="relative border-t border-purple-900/20 px-5 py-3"
              style={{ background: 'linear-gradient(90deg, rgba(60,20,120,0.2), rgba(0,0,0,0.6), rgba(60,20,120,0.2))' }}>
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.3), transparent)' }} />
              <div className="flex items-center justify-between font-mono text-[10px]">
                <div className="flex items-center gap-3 text-gray-700">
                  <SycekMark />
                  <span className="text-gray-800">Intelligence Platform · BTRA Engine v1</span>
                </div>
                <div className="flex items-center gap-4 text-gray-600">
                  <span className="text-gray-700">{meta?.reference}</span>
                  <span className={`px-2 py-0.5 rounded border font-bold text-[9px] ${tlpStyle(tlp)}`}>{tlp}</span>
                  <span>{meta?.generated_at?.slice(0, 19).replace('T', ' ')} UTC</span>
                </div>
              </div>
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
